import styles from './WidgetGroup.module.scss';

import { Add, Check, Close, Delete, Edit, MoreVert } from '@mui/icons-material';
import {
  ButtonBase,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Stack,
  Theme,
  useMediaQuery,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import clsx from 'clsx';
import { useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ChevronDownIcon } from '@work4all/assets/icons/chevron-down.svg';

import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';

import {
  FileViewMode,
  NO_WIDGET_GROUP_ID,
  useCustomBackgroundsContext,
} from '@work4all/data';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ILayoutDefinition } from '@work4all/models/lib/Layout';

import { settings, useSetting } from '../../../../../../settings';
import { FileContext } from '../../FileContext';
import { useFileWidgetSettings } from '../../widget-settings-dialog/use-file-widget-settings';
import { WidgetGroupContent } from '../WidgetGroupContent';

export type IWidgetGroupProps = {
  id: string;
  name: string;
  defaultCollapsed: boolean;
  widgets: ILayoutDefinition[];
  viewMode: FileViewMode;
  convertibleEntities?: Entities[];
};

export function WidgetGroup({
  id,
  name,
  defaultCollapsed,
  widgets,
  viewMode,
  convertibleEntities,
}: IWidgetGroupProps) {
  const isViewportDesktop = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.up('md')
  );

  const fileContext = useContext(FileContext);
  const { t } = useTranslation();

  const {
    filePageDeletedWidgetGroups,
    filePageWidgetGroupRegister,
    filePageWidgetGroupNameRegister,
    activeLayoutDefinition,
    addWidgetGroup,
  } = useFileWidgetSettings(fileContext.type);

  const collapsedWidgetGroup = useSetting(
    settings.collapsedWidgetGroup({
      fileType: fileContext.type,
      id,
      defaultValue: defaultCollapsed,
      layoutId: activeLayoutDefinition?.id,
    })
  );

  const customBackground = useCustomBackgroundsContext();

  const [menuOpen, setMenuOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedName, setEditedName] = useState(t(name));

  const menuBtnRef = useRef(null);
  const inputRef = useRef<HTMLDivElement>(null);

  const handleEnterEditMode = useCallback(() => {
    setEditMode(true);
    setMenuOpen(false);
    setTimeout(() => {
      inputRef.current.focus();
    });
  }, []);

  const handleCancelEditMode = useCallback(() => {
    setEditedName(t(name));
    setEditMode(false);
  }, [name, t]);

  const handleConfirmEditMode = useCallback(() => {
    filePageWidgetGroupNameRegister.set({
      ...filePageWidgetGroupNameRegister.value,
      [id]: editedName,
    });
    setEditMode(false);
  }, [editedName, filePageWidgetGroupNameRegister, id]);

  const handleDelete = useCallback(() => {
    const widgetsMovedToNoGroup = {};
    for (const widget of widgets) {
      widgetsMovedToNoGroup[widget.id] = NO_WIDGET_GROUP_ID;
    }
    filePageWidgetGroupRegister.set({
      ...filePageWidgetGroupRegister.value,
      ...widgetsMovedToNoGroup,
    });

    filePageDeletedWidgetGroups.set([...filePageDeletedWidgetGroups.value, id]);

    setMenuOpen(false);
  }, [filePageDeletedWidgetGroups, filePageWidgetGroupRegister, id, widgets]);

  if (id === NO_WIDGET_GROUP_ID) {
    return (
      <WidgetGroupContent
        widgets={widgets}
        viewMode={viewMode}
        groupId={id}
        convertibleEntities={convertibleEntities}
      />
    );
  }

  return (
    <div
      className={
        styles[!isViewportDesktop ? 'widget-group-md' : 'widget-group']
      }
    >
      <Stack direction="row" className={styles.headerWrap} alignItems="center">
        {editMode ? (
          <>
            <div
              ref={inputRef}
              contentEditable
              className={clsx(styles.editInput, {
                [styles['textInverse']]: customBackground.isActive,
              })}
              onKeyUp={() => {
                setEditedName(inputRef.current.textContent);
              }}
            >
              {t(name)}
            </div>
            <IconButton
              className={clsx(styles.btn)}
              onClick={handleCancelEditMode}
            >
              <Close />
            </IconButton>
            <IconButton
              className={clsx(styles.btn)}
              onClick={handleConfirmEditMode}
            >
              <Check />
            </IconButton>
          </>
        ) : (
          <>
            <ButtonBase
              className={clsx(styles['group-header-button'], {
                [styles['group-header-button-md']]: !isViewportDesktop,
                [styles['textInverse']]: customBackground.isActive,
              })}
              onClick={() => {
                collapsedWidgetGroup.set(!collapsedWidgetGroup.value);
              }}
            >
              <ChevronDownIcon
                className={clsx(
                  styles['icon'],
                  !collapsedWidgetGroup.value && styles['group-open']
                )}
              />
              <span>{t(name)}</span>
            </ButtonBase>
            <IconButton
              className={clsx(styles.btn, styles.editBtn)}
              ref={menuBtnRef}
              onClick={() => setMenuOpen(true)}
            >
              <MoreVert />
            </IconButton>
            <Menu
              open={menuOpen}
              anchorEl={menuBtnRef.current}
              onClose={() => setMenuOpen(false)}
            >
              <List disablePadding>
                <ListItemButton
                  onClick={handleEnterEditMode}
                  className={styles.menuItem}
                >
                  <ListItemIcon>
                    <Edit />
                  </ListItemIcon>
                  <ListItemText>{t('COMMON.EDIT')}</ListItemText>
                </ListItemButton>

                <ListItemButton
                  onClick={handleDelete}
                  className={styles.menuItem}
                >
                  <ListItemIcon>
                    <Delete />
                  </ListItemIcon>
                  <ListItemText>{t('WIDGET_GROUP.DELETE')}</ListItemText>
                </ListItemButton>

                <Divider />
                <ListItemButton
                  onClick={() => {
                    addWidgetGroup(t('MASK.GROUP_MANAGEMENT.NEW'));
                    setMenuOpen(false);
                  }}
                  className={styles.menuItem}
                >
                  <ListItemIcon>
                    <Add />
                  </ListItemIcon>
                  <ListItemText>{t('MASK.GROUP_MANAGEMENT.NEW')}</ListItemText>
                </ListItemButton>
              </List>
            </Menu>
          </>
        )}
      </Stack>

      <Collapse in={!collapsedWidgetGroup.value}>
        <WidgetGroupContent
          widgets={widgets}
          viewMode={viewMode}
          groupId={id}
          convertibleEntities={convertibleEntities}
          collapsed={collapsedWidgetGroup.value}
        />
      </Collapse>
    </div>
  );
}
